// React
import React, { useRef, useState, useEffect } from 'react';
import { useWindowSize } from '@react-hook/window-size';

// Prop types
import PropTypes from 'prop-types';
import * as appPropTypes from '../../appPropTypes';
import {
	meProducersSelector,
	spotlightPeersSelector
} from '../../Selectors';

// Redux
import { connect } from 'react-redux';
import { withRoomContext } from '../../../RoomContext';

// Component
import Peer from '../../Containers/Peer';
import classnames from 'classnames';
import VideoView from '../../VideoContainers/VideoView';

// Mui styles
import { withStyles } from '@material-ui/core/styles';

// Mui core
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import FullScreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';

const styles = () =>
	({
		root : {
			position        : 'absolute',
			top             : 0,
			left            : 0,
			width           : '100%',
			height          : '100%',
			display         : 'flex',
			flexDirection   : 'column',
			borderRadius    : '20px',
			backgroundColor : '#e5e5e2',
			zIndex          : 1499,
			padding         : '2%'
		},
		rootMobile : {
			padding        : '4%',
			justifyContent : 'space-between',
			'&.landscape'  : {
				height  : '100%',
				padding : '2%'
			},
			'&.fullScreen' : {
				padding      : '0',
				borderRadius : '0'
			}
		},
		screen : {
			width           : '100%',
			height          : '100%',
			backgroundColor : '#878686',
			borderRadius    : '20px',
			display         : 'flex',
			justifyContent  : 'center'
		},
		screenMobile : {
			width         : '100%',
			height        : '82%',
			'&.landscape' : {
				height : '100%'
			},
			'&.fullScreen' : {
				padding      : '0',
				borderRadius : '0',
				height       : '100%'
			}
		},
		notification : {
			zIndex          : '99',
			color           : 'var(--text-color)',
			fontSize        : '0.9rem',
			backgroundColor : '#e5e5e2',
			position        : 'absolute',
			top             : '1%',
			padding         : '0.5% 4%',
			borderRadius    : '20px'
		},
		notificationMobile : {
			top            : '0.5%',
			padding        : '1% 10%',
			borderRadius   : '8px',
			'&.fullScreen' : {
				top             : '2%',
				backgroundColor : '#878686',
				color           : 'white'
			}
		},
		fullScreenHostInitial : {
			zIndex   : '99',
			position : 'absolute',
			top      : '0.75%',
			right    : '0.75%'
		},
		fullScreenGuestInitial : {
			zIndex   : '99',
			position : 'absolute',
			top      : '0.25%',
			right    : '0.25%'
		},
		exitFullScreen : {
			zIndex   : '99',
			position : 'absolute',
			top      : '0.65%',
			right    : '0.65%'
		},
		stopShareBtn : {
			color           : 'white',
			backgroundColor : 'var(--next-button-color)',
			fontSize        : '0.8rem',
			marginLeft      : '1.5rem',
			marginTop       : '-0.25rem',
			padding         : '0.1rem 0.7rem',
			'&:hover'       : {
				backgroundColor : 'var(--next-button-color)'
			}
		},
		video : {
			flex               : '100 100 auto',
			height             : '100%',
			width              : '100%',
			objectFit          : 'contain',
			userSelect         : 'none',
			transitionProperty : 'opacity',
			transitionDuration : '.15s',
			backgroundColor    : 'black',
			borderRadius       : '20px',
			border             : 'none'
		},
		peer : {
			width                : '12vw',
			height               : '12vw',
			position             : 'absolute',
			paddingTop           : '1%',
			paddingRight         : '1%',
			bottom               : '0',
			backgroundColor      : '#e5e5e2',
			borderTopRightRadius : '15px',
			marginBottom         : '2%'
		},
		peerMobile : {
			width          : '100%',
			height         : '16%',
			display        : 'flex',
			justifyContent : 'space-between',
			'&.landscape'  : {
				height  : '28%',
				display : 'none'
			},
			'&.fullScreen' : {
				position             : 'absolute',
				bottom               : 0,
				width                : 'auto',
				borderTopRightRadius : '20px',
				backgroundColor      : '#878686',
				padding              : '1%'
			}
		},
		action : {
			width                         : '27%',
			order                         : '2',
			display                       : 'flex',
			justifyContent                : 'flex-end',
			'& > button:not(:last-child)' : {
				marginRight : '8%'
			}
		},
		screenShareButton : {
			width           : '30px',
			height          : '30px',
			borderRadius    : '50%',
			// backgroundColor : 'rgba(229, 229, 226, .8)',
			backgroundColor : '#e5e5e2',
			// backgroundColor : '#e26b04',
			// color           : '#FFF',
			color           : 'var(--text-color)',
			padding         : '3px',
			'&:hover'       : {
				backgroundColor : '#e5e5e2'
			}
		},
		fullScreenIcon : {
			fontSize : '1.75rem'
		},
		logo : {
			width  : '65%',
			height : 'auto'
		}
	});

const ScreenSharing = ({
	room,
	classes,
	roomClient,
	isMobile,
	isPortraitScreen,
	screenProducer,
	advancedMode,
	spotlightsPeers,
	consumer,
	fullScreenConsumer
}) =>
{
	const fullScreenElement = useRef(null);
	const [ isHost, setIsScreenSharing ] = useState(roomClient.isHost());
	const [ isFullScreen, setIsFullScreen ] = useState(false);

	const screenVisible = (
		Boolean(screenProducer) &&
		!screenProducer.locallyPaused &&
		!screenProducer.remotelyPaused
	);

	const meStyle =
	{
		'width'    : '100%',
		'height'   : '100%',
		'overflow' : 'hidden'
	};

	const peerMobileStyle =
	{
		'aspectRatio' : '1',
		'height'      : '100%',
		'overflow'    : 'hidden'
	};

	const handleStopSharing = () =>
	{
		setIsScreenSharing(false);
		roomClient.disableScreenSharing();
	};

	const goFullScreen = () =>
	{
		if (fullScreenElement.current.requestFullscreen)
		{
			fullScreenElement.current.requestFullscreen();
		}
		else if (fullScreenElement.current.msRequestFullscreen)
		{
			fullScreenElement.current.msRequestFullscreen();
		}
		else if (fullScreenElement.current.mozRequestFullScreen)
		{
			fullScreenElement.current.mozRequestFullScreen();
		}
		else if (fullScreenElement.current.webkitRequestFullscreen)
		{
			fullScreenElement.current.webkitRequestFullscreen();
		}
	};

	const leaveFullScreen = () =>
	{
		if (document.exitFullscreen)
		{
			document.exitFullscreen();
		}
		else if (document.msExitFullscreen)
		{
			document.msExitFullscreen();
		}
		else if (document.mozCancelFullScreen)
		{
			document.mozCancelFullScreen();
		}
		else if (document.webkitExitFullscreen)
		{
			document.webkitExitFullscreen();
		}
	};

	const handleFullScreen = () =>
	{
		if (isFullScreen)
		{
			leaveFullScreen();
		}
		else
		{
			goFullScreen();
		}
		setIsFullScreen(!isFullScreen);
	};

	const elementRef = useRef(null);
	const size = useWindowSize({
		wait : 400
	});

	useEffect(() =>
	{
		if (!elementRef.current)
			return;

		if (consumer && consumer.type !== 'simple')
		{
			room.adaptConsumerPreferredLayers(consumer, size[0], size[1]);
		}
	}, [ size, consumer, room, fullScreenConsumer ]);

	if (!isHost && !fullScreenConsumer)
	{
		return null;
	}

	const consumerVisible = (
		Boolean(consumer) &&
		!consumer.locallyPaused &&
		!consumer.remotelyPaused
	);

	let fullscreenBoxCss = classes.exitFullScreen;

	if (!isFullScreen)
	{
		if (isHost)
		{
			fullscreenBoxCss = classes.fullScreenHostInitial;
		}
		else
		{
			fullscreenBoxCss = classes.fullScreenGuestInitial;
		}

	}

	return (
		<Box
			ref={fullScreenElement}
			className={
				classnames(
					classes.root,
					isMobile ? classes.rootMobile : null,
					!isPortraitScreen ? 'landscape' : null,
					isFullScreen ? 'fullScreen' : null
				)
			}
		>
			<Box className={
				classnames(
					classes.screen,
					isMobile ? classes.screenMobile : null,
					!isPortraitScreen ? 'landscape' : null,
					isFullScreen ? 'fullScreen' : null
				)}
			>
				<Box className={
					classnames(
						classes.notification,
						isMobile ? classes.notificationMobile : null,
						isFullScreen ? 'fullScreen' : null
					)}
				>
					{ isHost ?
						<>
							あなたの画面が共有されています
							<Button
								className={classes.stopShareBtn}
								onClick={handleStopSharing}
							>
								画面共有を停止
							</Button>
						</> : '現在画面共有中です'
					}

				</Box>

				<Box className={fullscreenBoxCss}>

					{	isFullScreen ?

						<IconButton
							aria-label='fullScreenExit'
							className={classes.screenShareButton}
							onClick={handleFullScreen}
						>
							<FullscreenExitIcon className={classes.fullScreenIcon}/>
						</IconButton>
						:
						<IconButton
							aria-label='fullScreenExit'
							className={classes.screenShareButton}
							onClick={handleFullScreen}
						>
							<FullScreenIcon className={classes.fullScreenIcon}/>
						</IconButton>
					}
				</Box>

				{ isHost ?
					<VideoView
						isMe
						isScreen
						advancedMode={advancedMode}
						videoContain
						videoTrack={screenProducer && screenProducer.track}
						videoVisible={screenVisible}
						videoCodec={screenProducer && screenProducer.codec}
					/>
					:
					<VideoView
						advancedMode={advancedMode}
						videoContain
						consumerSpatialLayers={consumer ? consumer.spatialLayers : null}
						consumerTemporalLayers={consumer ? consumer.temporalLayers : null}
						consumerCurrentSpatialLayer={
							consumer ? consumer.currentSpatialLayer : null
						}
						consumerCurrentTemporalLayer={
							consumer ? consumer.currentTemporalLayer : null
						}
						consumerPreferredSpatialLayer={
							consumer ? consumer.preferredSpatialLayer : null
						}
						consumerPreferredTemporalLayer={
							consumer ? consumer.preferredTemporalLayer : null
						}
						videoMultiLayer={consumer && consumer.type !== 'simple'}
						videoTrack={consumer && consumer.track}
						videoVisible={consumerVisible}
						videoCodec={consumer && consumer.codec}
						videoScore={consumer ? consumer.score : null}
						width={size[0]}
						height={size[1]}
					/>
				}
			</Box>
			{ !isFullScreen &&
			<>
				<Box
					className={
						classnames(
							isMobile ? classes.peerMobile : classes.peer,
							!isPortraitScreen ? 'landscape' : null,
							isFullScreen ? 'fullScreen' : null
						)
					}
				>
					{ spotlightsPeers.map((peer) =>
					{
						return (
							<Peer
								key={peer}
								advancedMode={advancedMode}
								id={peer}
								rootStyle={isMobile ? peerMobileStyle : meStyle}
								style={isMobile ? peerMobileStyle : meStyle}
								enableLayersSwitch
							/>
						);
					})}
					{/* { isMobile && !isFullScreen &&
					<Box className={classes.action}>
						<Button
							className={
								classnames(
									classes.button,
									!isPortraitScreen ? 'landscape' : null
								)
							}
						>
							<img
								alt='logo1'
								src='images/change_screen_icon.png'
								className={classes.logo}
							/>
						</Button>
						<Button
							className={
								classnames(
									classes.button,
									!isPortraitScreen ? 'landscape' : null
								)
							}
						>
							<img
								alt='logo2'
								src='images/rotate_screen_icon.png'
								className={classes.logo}
							/>
						</Button>
					</Box>
					} */}
				</Box>
			</>
			}
		</Box>
	);
};

ScreenSharing.propTypes =
{
	classes            : PropTypes.object.isRequired,
	roomClient         : PropTypes.object.isRequired,
	room               : PropTypes.any.isRequired,
	isMobile           : PropTypes.bool.isRequired,
	isPortraitScreen   : PropTypes.bool.isRequired,
	advancedMode       : PropTypes.bool,
	screenProducer     : appPropTypes.Producer,
	spotlightsPeers    : PropTypes.array.isRequired,
	consumer           : appPropTypes.Consumer,
	fullScreenConsumer : PropTypes.string
};

const mapStateToProps = (state) =>
{
	return {
		room             : state.room,
		isMobile         : state.me.browser.platform === 'mobile' || state.me.useMobileView,
		isPortraitScreen : state.room.orientation ?
			state.room.orientation === 'portrait': state.me.useMobileOrientation === 'portrait',
		spotlightsPeers    : spotlightPeersSelector(state),
		consumer           : state.consumers[state.room.fullScreenConsumer],
		fullScreenConsumer : state.room.fullScreenConsumer,
		...meProducersSelector(state)
	};
};

const mapDispatchToProps = () =>
{
	return {};
};

export default withRoomContext(connect(
	mapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.room.orientation === next.room.orientation &&
				prev.me.useMobileOrientation === next.me.useMobileOrientation &&
				prev.producers === next.producers &&
				prev.peers === next.peers &&
				prev.consumers[prev.room.fullScreenConsumer] ===
					next.consumers[next.room.fullScreenConsumer]
			);
		}
	}
)(withStyles(styles)(ScreenSharing)));
